import { computedFrom, autoinject } from "aurelia-framework";
import { Router, Redirect, activationStrategy } from 'aurelia-router';
import { singleton } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { SimpleDialog } from "./dialogs/simple-dialog";
import { BusyService } from "../services/busy-service";
import { WebApiService } from "../services/web-api-service";
import { UpdateContactDialog } from "./dialogs/update-contact-dialog";
import { AppMessageService } from "../services/app-message-service";
import { SecurityService } from "../services/security-service";
import { Carrier } from "../models/carrier";
import 'jquery';
import * as moment from 'moment';
import { Contact } from "../models/contact";
import { ContextMenu, ContextMenuItem, ContextMenuDivider } from "./widgets/action-bar/context-menu";
import { CarrierDocument } from "../models/carrier-document";
import { AddRemoveCarrierDivisionDialog } from "./dialogs/add-remove-carrier-division";

@singleton()
@autoinject()
export class CarrierSummary {
    hasRosterChangeRole: boolean = false;

    constructor(
        private appMessageService: AppMessageService,
        private securityService: SecurityService,
        private dialogService: DialogService,
        private router: Router,
        private busyService: BusyService,
        private api: WebApiService) {

    }

    carrierId: number;
    carrier: Carrier = new Carrier();
    carrierMillCodes: Array<string>;
    carrierMillIds: Array<number>;
    carrierMillsString: string;
    isCarrierEnabledForMyMill: boolean;
    carrierInsurance: Array<any> = [];
    contacts: Array<any> = [];
    divisions: Array<any> = [];
    documents: Array<CarrierDocument> = [];

    async activate(params: any) {

        await this.securityService.init();

        this.hasRosterChangeRole = this.securityService.hasRole("request_roster_change");


        this.carrierId = +params.carrierId;

        if (this.carrierId) {
            return this.loadCarrierProfile(this.carrierId);
        }
    }

    attached() {
        //set the page title
        this.router.currentInstruction.config.navModel.setTitle(this.carrier.legalName);
        this.router.updateTitle();
    }

    refresh() {
        return this.loadCarrierProfile(this.carrierId);
    }

    loadCarrierProfile(carrierId: number) {

        this.appMessageService.clearPageScopedMessages();

        this.carrierId = carrierId;

        this.busyService.on();

        return Promise.all([
            this.loadCarrierDetails(carrierId),
            this.loadCarrierInsurance(carrierId),
            this.loadCarrierMills(carrierId),
            this.loadDivisions()
        ])
            .then(() => {
                this.loadCarrierContacts(carrierId);
                this.loadDocuments(this.carrier.uniqueId);

                //messages
                if (this.securityService.user.millCodes.length && !this.isCarrierEnabledForMyMill) {
                    if (this.securityService.hasRole("request_roster_change")) {
                        this.appMessageService.addInfoMessage("This carrier is not currently assigned to one of your divisions (" + this.securityService.user.millCodes
                            + ").  Use the action link below to assign the carrier to your division.");
                    } else {
                        this.appMessageService.addInfoMessage("This carrier is not currently assigned to one of your divisions (" + this.securityService.user.millCodes
                            + ").");
                    }
                }
            })
            .finally(() => this.busyService.off());
    }

    public isTrucksCarrier(){
        return this.carrier && (this.carrier.entityType == 'TRUCKS' || this.carrier.entityType == 'TRANSLOADER/TRUCKS');
    }

    getCarrierTypeForDisplay(carrier) {
        if (carrier.entityType == 'TRANSLOADER') {
            return "TRANSLOADER";
        } else {
            if (carrier.entityType != 'TRANSLOADER/TRUCKS' && carrier.typeOfAuthority == 'BOTH') {
                return 'PERMIT/BROKER';
            }
            return carrier.typeOfAuthority;
        }
    }

    private loadDocuments(carrierGuid) {
        return this.api.get("Carriers", "GetCarrierDocuments", { carrierGuid: carrierGuid })
            .then((result) => {
                this.documents = JSON.parse(result.response);
            });
    }

    loadCarrierDetails(carrierId: number) {
        return this.api.get("Carriers", "GetCarrierById", { carrierId: carrierId })
            .then((result) => {
                this.carrier = Object.assign(new Carrier(), JSON.parse(result.response));

                //this.carrierPointUrl = this.settingsService.settings["carrierPointUrl"].replace("${carrierPointId}", this.carrier.id);
            });
    }


    loadCarrierInsurance(carrierId: number) {
        this.busyService.on();
        return this.api.get("Carriers", "GetCarrierInsurance", { carrierId: carrierId })
            .then((result) => {
                this.carrierInsurance = JSON.parse(result.response);

                //set the "expired" property
                this.carrierInsurance.forEach(m => m.expired = moment(new Date()).isAfter(m.expiration, 'day'));

                //run business rules
                this.appMessageService.showWarningMessage("One or more insurance policies for this carrier have expired.",
                    this.carrier.status == 8 && this.carrierInsurance.find(m => m.expired));

            })
            .finally(() => this.busyService.off());
    }


    loadCarrierMills(carrierId: number) {
        return this.api.get("Carriers", "GetCarrierMills", { carrierId: carrierId })
            .then((result) => {
                let response = JSON.parse(result.response);
                this.carrierMillCodes = response.map((item) => {
                    return item.code;
                });
                this.carrierMillIds = response.map((item) => {
                    return item.id;
                });
                this.carrierMillsString = this.carrierMillCodes.join(",") || "[none]";
                this.isCarrierEnabledForMyMill = false;
                this.securityService.user.millCodes.forEach(myMillCode => {
                    if (this.carrierMillCodes.indexOf(myMillCode) > -1) this.isCarrierEnabledForMyMill = true;
                });
            });
    }


    loadDivisions() {
        this.busyService.on();
        return this.api.get("Registration", "GetDivisions")
            .then((result) => {
                this.divisions = JSON.parse(result.response);
            })
            .finally(() => {
                this.busyService.off();
            });
    }

    loadCarrierContacts(carrierId: number) {
        this.busyService.on();
        return this.api.get("Carriers", "GetCarrierContacts", { carrierId: carrierId })
            .then((result) => {
                var contacts = JSON.parse(result.response);
                this.contacts = contacts.filter(o => {
                    if (!o.mills) {
                        return true;
                    }
                    let answer = false;
                    o.mills.split(',').forEach(millCode => {
                        if (this.securityService.user.millCodes.indexOf(millCode) > -1) answer = true;
                    });
                    return answer;
                });;
            })
            .finally(() => {
                this.busyService.off();
            });
    }


    //isContactForThisMill(contact: Contact) {

    //    if (contact.contactTypeId == 1 || contact.contactTypeId == 2) {
    //        return true;
    //    }
    //    return false;

    //    //var myMillId = (this.divisions.find(item => item.code == this.securityService.user.millCode) || {}).id;

    //    //if (contact.millIds.indexOf(myMillId) > -1) {
    //    //    return false;
    //    //}
    //    //return true;
    //}

    // ACTIONS

    requestContactChanges() {

        //const millCodeText = this.securityService.user.millCodes.join(",");
        var model = {
            title: "Request contact changes for this carrier on my division roster.",
            message: "Please describe the contact information changes you are requesting for this carrier:",
            showComment: true,
            comment: "",
            buttons: ["Send Request", "Cancel"]
        };

        return this.dialogService.open(
            {
                viewModel: SimpleDialog,
                model: model
            })
            .whenClosed((result) => {
                if (result.output == "Send Request") {
                    this.busyService.on();
                    return this.api.post("Carriers", "RequestCarrierContactChanges",
                        {
                            carrierId: this.carrierId,
                            carrierName: this.carrier.dbaName || this.carrier.legalName,
                            comment: model.comment
                        })
                        .then((result) => {
                            this.appMessageService.addSuccessMessage("Request sent.");
                        })
                        .finally(() => {
                            this.busyService.off();
                        });
                }
            });

    }

    requestCarrierAdd(add) {

        var model = {
            add: add,
            carrierDivisions: this.carrierMillIds,
        };

        return this.dialogService.open(
            {
                viewModel: AddRemoveCarrierDivisionDialog,
                model: model
            })
            .whenClosed((result) => {
                if (result.output) {
                    this.busyService.on();
                    const divisionId = result.output;
                    return this.api.post("Carriers", "AddRemoveCarrierDivision",
                        {
                            add: add,
                            carrierId: this.carrierId,
                            divisionId: divisionId
                        })
                        .then((result) => {
                            const verb = add ? "add" : "remov";
                            this.appMessageService.addSuccessMessage("This carrier has been " + verb + "ed for your roster.  Please refresh the page.");
                        })
                        .finally(() => {
                            this.busyService.off();
                        });
                }
            });

    }

    @computedFrom("hasRosterChangeRole",
        "isCarrierEnabledForMyMill"
    )
    get canAddCarrier(): boolean {
        return this.hasRosterChangeRole;
    }
    @computedFrom("hasRosterChangeRole",
        "isCarrierEnabledForMyMill"
    )
    get canRemoveCarrier(): boolean {
        return this.hasRosterChangeRole && this.isCarrierEnabledForMyMill;
    }

    openContactDialog(contact: Contact) {
        this.dialogService.open({
            viewModel: UpdateContactDialog,
            model: {
                showMills: false,
                mills: this.divisions.filter((item) => this.carrierMillIds.indexOf(item.id) > -1),
                contact: contact,   //create a copy so that the original isn't modified
                carrierId: this.carrier.id
            }
        }).whenClosed((result) => {
            if (!result.wasCancelled) {
                //reload contacts
                this.loadCarrierContacts(this.carrier.id);
            }
        });

    }


}
